// Session-domain slice: the session list (with pagination) and the active
// session detail, plus list/detail reconciliation.
//
// Composed into useAgentStore via slice composition (single create()).
// Cross-slice calls go through get(), which resolves to the full store.

import {
  send_request,
} from "../../utils/Request";
import {
  critical as _crit,
} from "../../utils/ChatStreamLogger";
import {
  isSessionRowMetadataPending,
  normalizeSessionListSenders,
} from "./agentSessionsPanelUtils";
import {
  applySessionDetailToList,
  reconcileFetchedSessionList,
} from "./sessionListOptimistic";
import {
  _getLocalUserDisplayName,
} from "./agentStoreUtils";
import {
  _schedulePendingSessionListRefetch,
} from "./agentStreamLifecycle";
import useChatStreamStore from "./useChatStreamStore";

export function createSessionsSlice(set, get) {
  return {
  sessions: [],
  sessionsLoading: false,
  sessionsHasMore: true,
  sessionsLoadingMore: false,
  /** Server pagination offset for the next sessions page (not client list length). */
  sessionsNextOffset: 0,
  sessionDetail: null,
  sessionDetailLoading: false,

  fetchSessions: async (agentId, { silent = false, limit = 25 } = {}) => {
    if (!silent) set({ sessionsLoading: true });
    try {
      const query = limit > 0 ? { limit, offset: 0 } : {};
      const response = await send_request(
        `project-service/agent/${agentId}/sessions`,
        "",
        query,
        "GET"
      );
      const data = response?.data || [];
      const serverSessions = Array.isArray(data) ? data : [];
      const { sessions: priorSessions, _startingChatContext, sessionDetail } =
        get();
      const sessions = reconcileFetchedSessionList({
        priorSessions,
        serverSessions,
        agentId,
        startingChatContext: _startingChatContext,
        sessionDetail,
        localUserDisplayName: _getLocalUserDisplayName(),
      });
      set({
        sessions,
        sessionsHasMore: limit > 0 && serverSessions.length >= limit,
        sessionsNextOffset: serverSessions.length,
      });
      if (sessions.some(isSessionRowMetadataPending)) {
        _schedulePendingSessionListRefetch(agentId, get);
      }
    } catch (e) {
      set({
        error: e.message || "Failed to load sessions",
        sessions: [],
        sessionsHasMore: false,
        sessionsNextOffset: 0,
      });
    }
    if (!silent) set({ sessionsLoading: false });
  },

  fetchMoreSessions: async (agentId, { limit = 25 } = {}) => {
    const {
      sessionsLoadingMore,
      sessionsHasMore,
      sessions,
      sessionsNextOffset,
    } = get();
    if (sessionsLoadingMore || !sessionsHasMore) return;
    set({ sessionsLoadingMore: true });
    const offset = Number.isFinite(sessionsNextOffset)
      ? sessionsNextOffset
      : sessions.length;
    try {
      const response = await send_request(
        `project-service/agent/${agentId}/sessions`,
        "",
        { limit, offset },
        "GET"
      );
      const data = response?.data || [];
      const moreSessions = Array.isArray(data) ? data : [];
      const existingIds = new Set(sessions.map((s) => s.chatSessionId));
      const dedupedMore = moreSessions.filter((s) => !existingIds.has(s.chatSessionId));
      const normalizedMore = normalizeSessionListSenders(
        dedupedMore,
        _getLocalUserDisplayName()
      );
      const nextOffset = offset + moreSessions.length;
      set({
        sessions: [...sessions, ...normalizedMore],
        sessionsNextOffset: nextOffset,
        sessionsHasMore: moreSessions.length >= limit,
        sessionsLoadingMore: false,
      });
    } catch (_e) {
      set({ sessionsLoadingMore: false });
    }
  },

  // Fallback data loader — only used when SSE is unavailable (poll fallback,
  // submitted-recovery timeout). SSE snapshot is the primary data source.
  fetchSessionDetail: async (chatSessionId, { silent = false, force = false } = {}) => {
    const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
    const chatStatus = get().chatStatus;
    if (
      !force
      && silent
      && streamState === 'connected'
      && (chatStatus === 'submitted' || chatStatus === 'streaming')
    ) {
      return;
    }

    // The post-completion REST overwrite is now prevented by the reducer's
    // terminalApplied + epoch gate (§4.3): a REST snapshot from before/at the
    // terminal is rejected (restSupersededByLiveSse / restAfterTerminal) and the
    // _replaceMessages below is skipped because the input was not accepted (§4.5).
    _crit(
      "AgentStore",
      "info",
      `fetchSessionDetail session=${chatSessionId} silent=${silent} currentChatStatus=${chatStatus}`
    );
    if (!silent) set({ sessionDetailLoading: true });
    // Capture the turn epoch at fetch start so the reducer rejects this response
    // if a new turn opened (epoch bumped) while the request was in flight (§4.3).
    // The epoch gate is a stricter replacement for the old _latestFetchTarget
    // in-flight dedup: a stale response is rejected by the reducer (staleRestEpoch)
    // and its _replaceMessages is skipped.
    const fetchEpoch = get().lifecycle.turnEpoch;
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}`,
        "",
        {},
        "GET"
      );
      const detail = response?.data || null;
      if (detail && !detail.chatSessionId && !detail.messages) {
        if (!silent) set({ sessionDetailLoading: false });
        return;
      }

      // §4.3/§4.5: gate the REST snapshot through the sequence-gated reducer. The
      // reducer is the single owner of chatStatus and decides whether this REST is
      // authoritative: it is rejected when SSE is the live source for the turn
      // (restSupersededByLiveSse), a terminal was already applied this epoch
      // (restAfterTerminal), or the epoch advanced mid-flight (staleRestEpoch), and
      // accepted on cold load / poll fallback. The old "stale IN_PROGRESS after
      // completion" heuristic is subsumed by restAfterTerminal. We must NOT
      // overwrite the live transcript with a lagging snapshot, so _replaceMessages
      // runs only when the input was accepted.
      const restAccepted = get()._applyLifecycle({
        kind: 'rest',
        sessionId: chatSessionId,
        fetchEpoch,
        backendStatus: detail?.status ?? null,
      });
      if (restAccepted) {
        get()._replaceMessages(chatSessionId, detail?.messages, detail);
      }
    } catch (e) {
      set((s) => ({
        error: e.message || "Failed to load session detail",
        sessionDetail: s.sessionDetail || null,
      }));
    }
    // Always clear sessionDetailLoading — even for silent fetches, since the
    // SSE-first flow sets sessionDetailLoading:true and relies on this fallback
    // fetch to clear it if SSE never connects.
    set({ sessionDetailLoading: false });
  },

  _syncSessionListFromDetail: (chatSessionId) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) return {};
      return {
        sessions: applySessionDetailToList(s.sessions, detail, {
          clearOptimistic: false,
        }),
      };
    });
  },

  refreshSessionInList: (chatSessionId) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) return {};
      return {
        sessions: applySessionDetailToList(s.sessions, detail, {
          clearOptimistic: true,
        }),
      };
    });
  },
  };
}
